/**
 * CREATIVE DETONATOR - Event Manager
 * Centralized event handling with automatic cleanup
 *
 * @version 2.0.0
 * @description Implements FRONTEND-23 (Event Listener Cleanup)
 *              + REFACTOR-03 (Event Bus Decoupling)
 *
 * This module provides two types of event handling:
 *
 * 1. DOM Events (existing functionality):
 *    - on(element, event, handler, scope) - Add DOM listener with scope
 *    - cleanupScope(scope) - Remove all listeners in a scope
 *
 * 2. Application Events (new in v2.0):
 *    - emit(eventName, detail) - Dispatch app event
 *    - subscribe(eventName, handler, options) - Listen for app events
 *    - off(eventName, handler) - Remove app event listener
 *    - Domain emitters: IdeaEvents, SessionEvents, etc.
 */

// ============================================================================
// APPLICATION EVENT BUS (REFACTOR-03)
// ============================================================================

/**
 * Application event listeners storage
 * Map<eventName, Set<{handler, scope, once}>>
 */
const appListeners = new Map();

/**
 * Scoped app event listener tracking for cleanup
 * Map<scope, Array<{eventName, listener}>>
 */
const scopedAppListeners = new Map();

/**
 * Subscribe to an application event
 * @param {string} eventName - Event name (e.g., 'idea:updated')
 * @param {Function} handler - Event handler function
 * @param {Object} [options]
 * @param {string} [options.scope='global'] - Scope for cleanup
 * @param {boolean} [options.once=false] - Remove after first call
 * @returns {Function} Unsubscribe function
 *
 * @example
 * // Basic usage
 * subscribe('idea:updated', ({ idea }) => updateCard(idea));
 *
 * // With scope for cleanup
 * subscribe('idea:updated', handler, { scope: 'canvas' });
 * cleanupScope('canvas'); // Removes the listener
 *
 * // One-time listener
 * subscribe('generator:completed', onComplete, { once: true });
 */
export function subscribe(eventName, handler, options = {}) {
  const { scope = 'global', once = false } = options;

  if (!appListeners.has(eventName)) {
    appListeners.set(eventName, new Set());
  }

  const listener = { handler, scope, once };
  appListeners.get(eventName).add(listener);

  // Track in scope for cleanup
  if (scope !== 'global') {
    if (!scopedAppListeners.has(scope)) {
      scopedAppListeners.set(scope, []);
    }
    scopedAppListeners.get(scope).push({ eventName, listener });
  }

  // Return unsubscribe function
  return () => {
    appListeners.get(eventName)?.delete(listener);
    // Also remove from scoped tracking
    const scopedList = scopedAppListeners.get(scope);
    if (scopedList) {
      const idx = scopedList.findIndex(item =>
        item.eventName === eventName && item.listener === listener
      );
      if (idx > -1) scopedList.splice(idx, 1);
    }
  };
}

/**
 * Subscribe to event once (convenience wrapper)
 * @param {string} eventName
 * @param {Function} handler
 * @param {Object} [options]
 * @returns {Function} Unsubscribe function
 */
export function subscribeOnce(eventName, handler, options = {}) {
  return subscribe(eventName, handler, { ...options, once: true });
}

/**
 * Unsubscribe from an application event
 * @param {string} eventName - Event name
 * @param {Function} handler - The handler function to remove
 */
export function off(eventName, handler) {
  const listeners = appListeners.get(eventName);
  if (!listeners) return;

  for (const listener of listeners) {
    if (listener.handler === handler) {
      listeners.delete(listener);
      break;
    }
  }
}

/**
 * Emit an application event
 * @param {string} eventName - Event name (e.g., 'idea:updated')
 * @param {Object} [detail={}] - Event data
 *
 * @example
 * emit('idea:updated', { idea: updatedIdea });
 * emit('auth:credits-insufficient', { required: 100, available: 50 });
 */
export function emit(eventName, detail = {}) {
  const listeners = appListeners.get(eventName);

  if (!listeners || listeners.size === 0) {
    // Log in dev if no listeners (might indicate typo)
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV && !eventName.startsWith('_')) {
      console.debug(`[Events] No listeners for: ${eventName}`);
    }
    return;
  }

  const toRemove = [];

  for (const listener of listeners) {
    try {
      listener.handler(detail);
      if (listener.once) {
        toRemove.push(listener);
      }
    } catch (err) {
      console.error(`[Events] Error in handler for ${eventName}:`, err);
    }
  }

  // Remove one-time listeners
  for (const listener of toRemove) {
    listeners.delete(listener);
  }
}

/**
 * Check if an event has any listeners
 * @param {string} eventName
 * @returns {boolean}
 */
export function hasListeners(eventName) {
  return (appListeners.get(eventName)?.size || 0) > 0;
}

/**
 * Get all event names with active listeners
 * @returns {string[]}
 */
export function getActiveEvents() {
  const events = [];
  for (const [name, listeners] of appListeners) {
    if (listeners.size > 0) {
      events.push(name);
    }
  }
  return events;
}

/**
 * Create a typed event emitter for a domain
 * Provides type-safe event emission/subscription
 *
 * @param {string} domain - Event domain (e.g., 'idea', 'session')
 * @returns {Object} Domain emitter with emit, on, once, off methods
 *
 * @example
 * const IdeaEvents = createDomainEmitter('idea');
 * IdeaEvents.emit('updated', { idea }); // Emits 'idea:updated'
 * IdeaEvents.on('updated', handler);     // Subscribes to 'idea:updated'
 */
export function createDomainEmitter(domain) {
  return {
    /**
     * Emit a domain event
     * @param {string} action - Event action (e.g., 'updated')
     * @param {Object} detail - Event data
     */
    emit(action, detail = {}) {
      emit(`${domain}:${action}`, detail);
    },

    /**
     * Subscribe to a domain event
     * @param {string} action - Event action
     * @param {Function} handler - Event handler
     * @param {Object} [options] - Subscription options
     * @returns {Function} Unsubscribe function
     */
    on(action, handler, options = {}) {
      return subscribe(`${domain}:${action}`, handler, options);
    },

    /**
     * Subscribe once to a domain event
     * @param {string} action - Event action
     * @param {Function} handler - Event handler
     * @param {Object} [options] - Subscription options
     * @returns {Function} Unsubscribe function
     */
    once(action, handler, options = {}) {
      return subscribeOnce(`${domain}:${action}`, handler, options);
    },

    /**
     * Unsubscribe from a domain event
     * @param {string} action - Event action
     * @param {Function} handler - Handler to remove
     */
    off(action, handler) {
      off(`${domain}:${action}`, handler);
    }
  };
}

// ============================================================================
// PRE-CONFIGURED DOMAIN EMITTERS
// ============================================================================

/** Idea lifecycle events (created, updated, deleted, saved, etc.) */
export const IdeaEvents = createDomainEmitter('idea');

/** Session events (created, loaded, completed, etc.) */
export const SessionEvents = createDomainEmitter('session');

/** Generator workflow events (phase-changed, generation-started, etc.) */
export const GeneratorEvents = createDomainEmitter('generator');

/** Image generation events (generation-started, generation-completed, etc.) */
export const ImageEvents = createDomainEmitter('image');

/** Video generation events (generation-started, polling-update, etc.) */
export const VideoEvents = createDomainEmitter('video');

/** Auth events (login, logout, credits-updated, etc.) */
export const AuthEvents = createDomainEmitter('auth');

/** UI component events (modal-opened, canvas-opened, etc.) */
export const UIEvents = createDomainEmitter('ui');

/** Navigation events (page-loaded, route-changed) */
export const NavEvents = createDomainEmitter('nav');

/** Storyboard events (loaded, scene-updated, etc.) */
export const StoryboardEvents = createDomainEmitter('storyboard');

// ============================================================================
// ABORT CONTROLLER REGISTRY
// ============================================================================

const controllers = new Map();

/**
 * Create or get AbortController for a scope
 * @param {string} scope - Scope identifier (e.g., 'modal', 'phase4', 'presentation')
 * @returns {AbortController}
 */
export function getController(scope) {
  if (!controllers.has(scope)) {
    controllers.set(scope, new AbortController());
  }
  return controllers.get(scope);
}

/**
 * Abort all listeners in a scope (DOM events + app events)
 * @param {string} scope - Scope to cleanup
 */
export function cleanupScope(scope) {
  // Cleanup DOM event listeners
  const controller = controllers.get(scope);
  if (controller) {
    controller.abort();
    controllers.delete(scope);
  }

  // Cleanup app event listeners (REFACTOR-03)
  const scopedList = scopedAppListeners.get(scope);
  if (scopedList) {
    for (const { eventName, listener } of scopedList) {
      appListeners.get(eventName)?.delete(listener);
    }
    scopedAppListeners.delete(scope);
  }
}

/**
 * Abort all scopes (DOM events + app events)
 */
export function cleanupAll() {
  // Cleanup DOM event listeners
  for (const [scope, controller] of controllers) {
    controller.abort();
  }
  controllers.clear();

  // Cleanup app event listeners (REFACTOR-03)
  appListeners.clear();
  scopedAppListeners.clear();
}

/**
 * Check if a scope exists and is active
 * @param {string} scope
 * @returns {boolean}
 */
export function hasScope(scope) {
  return controllers.has(scope) && !controllers.get(scope).signal.aborted;
}

// ============================================================================
// SCOPED EVENT LISTENER
// ============================================================================

/**
 * Add event listener with automatic scope tracking
 * @param {Element|Window|Document} element - Target element
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 * @param {string|Object} options - Scope string or options object
 * @returns {Function} Remove listener function
 *
 * @example
 * // Simple usage with scope
 * on(button, 'click', handleClick, 'modal');
 *
 * // With options
 * on(input, 'input', handleInput, { scope: 'form', capture: true });
 *
 * // Later: cleanupScope('modal') removes all 'modal' scoped listeners
 */
export function on(element, event, handler, options = {}) {
  const opts = typeof options === 'string'
    ? { scope: options }
    : options;

  const { scope = 'global', ...listenerOptions } = opts;

  // Get AbortController for this scope
  const controller = getController(scope);

  // Add signal to listener options
  element.addEventListener(event, handler, {
    ...listenerOptions,
    signal: controller.signal
  });

  // Return manual remove function (if needed before scope cleanup)
  return () => {
    element.removeEventListener(event, handler, listenerOptions);
  };
}

/**
 * Add multiple event listeners to same element
 * @param {Element} element
 * @param {Object} events - { eventType: handler }
 * @param {string} scope
 * @returns {Function} Remove all listeners function
 */
export function onMultiple(element, events, scope = 'global') {
  const removers = [];

  for (const [event, handler] of Object.entries(events)) {
    removers.push(on(element, event, handler, scope));
  }

  return () => removers.forEach(remove => remove());
}

// ============================================================================
// SPECIALIZED LISTENERS
// ============================================================================

/**
 * Add window listener with scope
 * @param {string} event
 * @param {Function} handler
 * @param {Object|string} options
 */
export function onWindow(event, handler, options = {}) {
  return on(window, event, handler, options);
}

/**
 * Add document listener with scope
 * @param {string} event
 * @param {Function} handler
 * @param {Object|string} options
 */
export function onDocument(event, handler, options = {}) {
  return on(document, event, handler, options);
}

/**
 * One-time listener (auto-removes after first call)
 * @param {Element} element
 * @param {string} event
 * @param {Function} handler
 * @param {Object|string} options
 */
export function once(element, event, handler, options = {}) {
  const opts = typeof options === 'string' ? { scope: options } : options;
  return on(element, event, handler, { ...opts, once: true });
}

// ============================================================================
// KEYBOARD SHORTCUTS
// ============================================================================

const shortcuts = new Map();
let shortcutListenerAdded = false;

/**
 * Register keyboard shortcut
 * @param {string} key - Key or key combo (e.g., 'p', 'ctrl+s', 'escape', 'shift+?')
 * @param {Function} handler - Handler function
 * @param {Object} options - Options
 * @returns {Function} Unregister function
 *
 * @example
 * // Simple key
 * registerShortcut('p', () => openPresentation(), { scope: 'ideas' });
 *
 * // Key combo
 * registerShortcut('ctrl+s', () => save(), { scope: 'editor' });
 *
 * // With options
 * registerShortcut('escape', closeModal, { scope: 'modal', preventDefault: true });
 */
export function registerShortcut(key, handler, options = {}) {
  const {
    scope = 'global',
    preventDefault = true,
    ignoreInputs = true
  } = options;

  const normalizedKey = key.toLowerCase();

  // Initialize document listener if not done
  if (!shortcutListenerAdded) {
    document.addEventListener('keydown', handleShortcuts);
    shortcutListenerAdded = true;
  }

  if (!shortcuts.has(normalizedKey)) {
    shortcuts.set(normalizedKey, []);
  }

  const entry = { handler, scope, preventDefault, ignoreInputs };
  shortcuts.get(normalizedKey).push(entry);

  // Return unregister function
  return () => {
    const handlers = shortcuts.get(normalizedKey);
    if (handlers) {
      const index = handlers.indexOf(entry);
      if (index > -1) handlers.splice(index, 1);
      if (handlers.length === 0) {
        shortcuts.delete(normalizedKey);
      }
    }
  };
}

/**
 * Handle all keyboard shortcuts
 * @private
 */
function handleShortcuts(e) {
  // Build key string
  const parts = [];
  if (e.ctrlKey) parts.push('ctrl');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  if (e.metaKey) parts.push('meta');
  parts.push(e.key.toLowerCase());
  const keyString = parts.join('+');

  // Also check just the key (for shortcuts without modifiers)
  const justKey = e.key.toLowerCase();

  // Find matching handlers
  const matchingEntries = shortcuts.get(keyString) || shortcuts.get(justKey) || [];

  for (const entry of matchingEntries) {
    const { handler, scope, preventDefault: pd, ignoreInputs } = entry;

    // Check if should ignore when input focused
    if (ignoreInputs && isInputFocused()) continue;

    // Check if scope is active
    if (scope !== 'global' && !hasScope(scope)) continue;

    // Execute handler
    if (pd) e.preventDefault();
    handler(e);
  }
}

/**
 * Check if input element is focused
 * @private
 */
function isInputFocused() {
  const active = document.activeElement;
  return active && (
    active.tagName === 'INPUT' ||
    active.tagName === 'TEXTAREA' ||
    active.isContentEditable
  );
}

/**
 * Clear all shortcuts for a scope
 * @param {string} scope
 */
export function clearShortcuts(scope) {
  for (const [key, handlers] of shortcuts) {
    const filtered = handlers.filter(h => h.scope !== scope);
    if (filtered.length === 0) {
      shortcuts.delete(key);
    } else {
      shortcuts.set(key, filtered);
    }
  }
}

// ============================================================================
// DEBOUNCED/THROTTLED LISTENERS
// ============================================================================

/**
 * Add debounced listener
 * @param {Element} element
 * @param {string} event
 * @param {Function} handler
 * @param {number} delay - Debounce delay in ms
 * @param {Object|string} options
 */
export function onDebounced(element, event, handler, delay = 300, options = {}) {
  let timeoutId;

  const debouncedHandler = (e) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => handler(e), delay);
  };

  return on(element, event, debouncedHandler, options);
}

/**
 * Add throttled listener
 * @param {Element} element
 * @param {string} event
 * @param {Function} handler
 * @param {number} limit - Throttle limit in ms
 * @param {Object|string} options
 */
export function onThrottled(element, event, handler, limit = 100, options = {}) {
  let lastRun = 0;
  let timeoutId;

  const throttledHandler = (e) => {
    const now = Date.now();

    if (now - lastRun >= limit) {
      lastRun = now;
      handler(e);
    } else if (!timeoutId) {
      timeoutId = setTimeout(() => {
        lastRun = Date.now();
        timeoutId = null;
        handler(e);
      }, limit - (now - lastRun));
    }
  };

  return on(element, event, throttledHandler, options);
}

// ============================================================================
// RESIZE/INTERSECTION OBSERVERS
// ============================================================================

const resizeObservers = new Map();

/**
 * Observe element resize
 * @param {Element} element
 * @param {Function} handler
 * @param {string} scope
 * @returns {Function} Stop observing function
 */
export function onResize(element, handler, scope = 'global') {
  if (!resizeObservers.has(scope)) {
    resizeObservers.set(scope, new ResizeObserver((entries) => {
      for (const entry of entries) {
        const handlers = entry.target.__resizeHandlers || [];
        handlers.forEach(h => h(entry));
      }
    }));
  }

  const observer = resizeObservers.get(scope);

  // Store handler on element
  if (!element.__resizeHandlers) {
    element.__resizeHandlers = [];
    observer.observe(element);
  }
  element.__resizeHandlers.push(handler);

  // Return cleanup function
  return () => {
    const handlers = element.__resizeHandlers;
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) handlers.splice(index, 1);
      if (handlers.length === 0) {
        observer.unobserve(element);
        delete element.__resizeHandlers;
      }
    }
  };
}

/**
 * Cleanup resize observers for scope
 * @param {string} scope
 */
export function cleanupResizeObservers(scope) {
  const observer = resizeObservers.get(scope);
  if (observer) {
    observer.disconnect();
    resizeObservers.delete(scope);
  }
}

// ============================================================================
// EVENT DELEGATION
// ============================================================================

/**
 * Delegate events to parent element
 * @param {Element} parent - Parent element to attach listener to
 * @param {string} event - Event type
 * @param {string} selector - CSS selector for target elements
 * @param {Function} handler - Handler function (receives event and matched element)
 * @param {Object|string} options
 * @returns {Function} Remove listener function
 *
 * @example
 * // Instead of adding listener to each button
 * delegate(container, 'click', '.btn', (e, btn) => {
 *   console.log('Button clicked:', btn.dataset.id);
 * }, 'myScope');
 */
export function delegate(parent, event, selector, handler, options = {}) {
  const delegatedHandler = (e) => {
    const target = e.target.closest(selector);
    if (target && parent.contains(target)) {
      handler(e, target);
    }
  };

  return on(parent, event, delegatedHandler, options);
}

// ============================================================================
// DEBUG UTILITIES (development only)
// ============================================================================

/**
 * Debug utilities for development
 * Accessible via window.__eventDebug in dev mode
 */
if (typeof window !== 'undefined') {
  window.__eventDebug = {
    getActiveEvents,
    getListenerCount: (name) => appListeners.get(name)?.size || 0,
    listAllListeners: () => {
      const result = {};
      for (const [name, listeners] of appListeners) {
        result[name] = listeners.size;
      }
      return result;
    },
    listScopes: () => Array.from(scopedAppListeners.keys()),
    getScopedListenerCount: (scope) => scopedAppListeners.get(scope)?.length || 0
  };
}

// ============================================================================
// GLOBAL EXPORT
// ============================================================================

window.Events = {
  // DOM events (existing)
  on,
  onMultiple,
  onWindow,
  onDocument,
  once,
  onDebounced,
  onThrottled,
  onResize,
  delegate,
  registerShortcut,
  clearShortcuts,
  cleanupScope,
  cleanupAll,
  cleanupResizeObservers,
  getController,
  hasScope,
  // App events (REFACTOR-03)
  emit,
  subscribe,
  subscribeOnce,
  off,
  hasListeners,
  getActiveEvents,
  createDomainEmitter,
  // Domain emitters
  IdeaEvents,
  SessionEvents,
  GeneratorEvents,
  ImageEvents,
  VideoEvents,
  AuthEvents,
  UIEvents,
  NavEvents,
  StoryboardEvents
};

export default {
  // DOM events (existing)
  on,
  onMultiple,
  onWindow,
  onDocument,
  once,
  onDebounced,
  onThrottled,
  onResize,
  delegate,
  registerShortcut,
  clearShortcuts,
  cleanupScope,
  cleanupAll,
  cleanupResizeObservers,
  getController,
  hasScope,
  // App events (REFACTOR-03)
  emit,
  subscribe,
  subscribeOnce,
  off,
  hasListeners,
  getActiveEvents,
  createDomainEmitter,
  // Domain emitters
  IdeaEvents,
  SessionEvents,
  GeneratorEvents,
  ImageEvents,
  VideoEvents,
  AuthEvents,
  UIEvents,
  NavEvents,
  StoryboardEvents
};
